import moment from 'moment-timezone'
import { getSeparationStatusLabel } from '@/modules/profile/utils/separation'

const STATUS_COLORS = {
  completed: '#10b981',
  inProgress: '#3b82f6',
  awaitingReview: '#f59e0b',
  danger: '#ef4444',
  pending: '#6b7280',
  muted: '#6b7280'
}

const PERIOD_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4']

const ONBOARDING_EVENT_TYPES = ['onboarding_assigned', 'onboarding_completed', 'onboarding_approved']
const ORIENTATION_EVENT_TYPES = ['orientation_assigned', 'orientation_completed', 'orientation_approved']
const REVIEW_EVENT_TYPES = ['onboarding_completed', 'orientation_completed']
const VIEW_EVENT_TYPES = ['onboarding_approved', 'orientation_approved']
const DOCUMENT_EVENT_TYPES = [
  'signed_offer_letter',
  'signed_w9_form',
  'signed_temporary_service_agreement',
  'signed_resignation_letter',
  'signed_termination_letter',
  'performance_review_signed',
  'annual_skills_check_signed',
  'initial_skills_check_signed'
]
const EXPORT_EVENT_FILTERS = {
  performance_review_signed: 'performance_review',
  annual_skills_check_signed: 'annual_skills_check',
  initial_skills_check_signed: 'skills_check'
}
const START_LABELS = {
  invitation_sent: 'Invited to project',
  invitation_accepted: 'Invited to project',
  assigned_to_client: 'Assigned to client',
  created_from_scheduler: 'Created from Schedule',
  cloned: 'Cloned'
}
const EVENT_NAME_OVERRIDES = {
  training_period_start: 'Training Period Started',
  training_period_end: 'Training Period Ended',
  initial_skills_check_signed: 'Initial Skill Check',
  annual_skills_check_signed: 'Annual Skill Check',
  performance_review_signed: 'Performance Review'
}
const STEP_DESCRIPTIONS = {
  Created: 'The associate has used ePeople from this date.',
  'Invited to project': 'The associate was invited to join this project in ePeople.',
  'Assigned to client': 'The associate was assigned to this company in ePeople.',
  'Created from Schedule': 'The associate was created from Schedule on this date.',
  Cloned: 'The associate profile was copied from another project or company record.',
  'First Shift': 'The associate completed their first shift on this date.',
  Seniority: 'The associate has worked with the company from this date.',
  Onboarding: 'The employee completes required onboarding steps before being approved to work.',
  Orientation: 'The contractor completes required orientation steps before being approved to work.',
  'Initial Skill Check': 'The associate completes the training period and signs the Initial Skill Check document.',
  'Annual Skill Check': 'The associate completes an Annual Skill Check each year.',
  'Performance Review': 'The associate completes a yearly performance review.',
  Training: "This shows the associate's progress through assigned trainings.",
  'Change Action Forms': 'Compensation changes that require the associate acknowledgement.',
  Separation: "The associate's work with the company ended or is scheduled to end.",
  Rehire: 'The associate started a new employment cycle after returning to the company.'
}
const INITIAL_SKILL_CHECK_EVENT_TYPE = 'initial_skills_check_signed'
const ANNUAL_SKILL_CHECK_EVENT_TYPE = 'annual_skills_check_signed'
const PERFORMANCE_REVIEW_EVENT_TYPE = 'performance_review_signed'
const TRAINING_PERIOD_EVENT_TYPES = ['training_period_start', 'training_period_end']
const FINAL_STATUSES = ['approved', 'completed', 'complete', 'signed', 'accepted', 'actual']
const DANGER_STATUSES = ['rejected', 'declined', 'expired', 'missing']

export default {
  computed: {
    normalizedPaymentType() {
      return String(this.associate?.payment_info?.type ?? '').toLowerCase()
    },

    middleStepTitle() {
      return this.normalizedPaymentType === 'contractor' ? 'Orientation' : 'Onboarding'
    },

    expectedMiddleEventTypes() {
      return this.normalizedPaymentType === 'contractor' ? ORIENTATION_EVENT_TYPES : ONBOARDING_EVENT_TYPES
    }
  },

  methods: {
    getPeriodKey(index) {
      return `lifecycle-period-${index}`
    },

    buildLifecyclePeriods() {
      return this.lifecycle.map((period, index) => this.buildPeriod(period, index))
    },

    isPeriodExpanded(periodKey) {
      return !this.canCollapsePeriods || this.expandedPeriodKeys.includes(periodKey)
    },

    isDynamicFormAvailable(type) {
      return Boolean(this.dynamicFormAvailability?.[type])
    },

    formatDate(date) {
      if (!date) return undefined

      const parsed = this.parseDate(date)

      return parsed ? parsed.format('MM/DD/YYYY') : date
    },

    parseDate(date) {
      if (!date) return undefined

      const value = String(date)
      const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value) ? moment.utc(value, 'YYYY-MM-DD', true) : moment(date).utc()

      return parsed.isValid() ? parsed.utc().startOf('day') : undefined
    },

    addYear(date) {
      const parsed = this.parseDate(date)

      return parsed ? parsed.clone().add(1, 'year').toISOString() : undefined
    },

    isDueDate(date) {
      const parsed = this.parseDate(date)

      return parsed ? moment().utc().startOf('day').isSameOrAfter(parsed, 'day') : false
    },

    formatAuthor(author) {
      if (!author) return 'System'

      const fullName = [author.first_name, author.last_name].filter(Boolean).join(' ').trim()

      return fullName || author.email || 'System'
    },

    sortEvents(events) {
      return [...events].sort((first, second) => {
        const firstDate = moment(first.created_at)
        const secondDate = moment(second.created_at)

        if (firstDate.isValid() && secondDate.isValid() && !firstDate.isSame(secondDate)) {
          return firstDate.valueOf() - secondDate.valueOf()
        }

        return Number(first.id ?? 0) - Number(second.id ?? 0)
      })
    },

    getEventType(event) {
      return String(event.sub_type ?? '')
        .trim()
        .toLowerCase()
    },

    buildEvent(event, groupKey, index) {
      const objectType = String(event.object_type ?? '').toLowerCase()
      const eventType = this.getEventType(event)
      const metadata = event.metadata ?? {}
      const isDocument = objectType === 'projectdocversionsign' || DOCUMENT_EVENT_TYPES.includes(eventType)
      const isSequence = objectType.includes('sequence')
      const canOpenDocument =
        isDocument && metadata.document_id && metadata.version_number !== undefined && metadata.version_number !== null
      const canOpenSequence = isSequence && event.object_id

      return {
        ...event,
        key: `${groupKey}-${event.id || event.object_id || index}`,
        eventType,
        name: EVENT_NAME_OVERRIDES[eventType] || event.name || 'Untitled event',
        meta: [this.formatDate(event.created_at), this.formatAuthor(event.author), event.reason]
          .filter(Boolean)
          .join(' - '),
        typeLabel: metadata.document_type || metadata.sequence_type || metadata.termination_type,
        canOpen: Boolean(canOpenDocument || canOpenSequence),
        openType: canOpenDocument ? 'document' : canOpenSequence ? 'sequence' : undefined,
        actions: this.buildEventActions({
          canOpenDocument,
          canOpenSequence,
          eventType,
          metadata,
          name: event.name,
          objectType
        })
      }
    },

    buildEvents(events, groupKey) {
      return this.sortEvents(events ?? []).map((event, index) => this.buildEvent(event, groupKey, index))
    },

    buildEventActions({ canOpenDocument, canOpenSequence, eventType, metadata, name, objectType }) {
      const actions = []
      const exportFilter = EXPORT_EVENT_FILTERS[eventType]

      if (canOpenDocument || (canOpenSequence && this.canShowSequenceAction(eventType))) {
        const actionLabel =
          metadata.action_label ||
          metadata.action ||
          (canOpenDocument ? 'View' : this.getSequenceActionLabel({ eventType, name, objectType }))

        actions.push({ key: 'open', label: actionLabel })
      }

      if (exportFilter) {
        actions.push({ key: 'download-export', label: 'Download', filter: exportFilter })
        actions.push({ key: 'send-export', label: 'Send To Email', filter: exportFilter })
      }

      const downloadUrl = metadata.download_url || metadata.file_url || metadata.url

      if (downloadUrl) {
        actions.push({ key: 'download-url', label: 'Download', url: downloadUrl })
      }

      return actions
    },

    canShowSequenceAction(eventType) {
      return REVIEW_EVENT_TYPES.includes(eventType) || VIEW_EVENT_TYPES.includes(eventType)
    },

    getSequenceActionLabel({ eventType, name, objectType }) {
      if (REVIEW_EVENT_TYPES.includes(eventType)) return 'Review'
      if (VIEW_EVENT_TYPES.includes(eventType)) return 'View'

      const normalizedName = String(name ?? '').toLowerCase()

      return normalizedName.includes('review') || objectType.includes('review') ? 'Review' : 'Open'
    },

    getTerminationStatusTitle(events) {
      const terminationEvent = events.find(event => this.getEventType(event) === 'associate_terminated')

      return terminationEvent ? getSeparationStatusLabel(terminationEvent.metadata?.termination_type) : ''
    },

    getLatestDate(events) {
      return events
        .map(event => event.created_at)
        .filter(Boolean)
        .slice(-1)[0]
    },

    buildStep({
      key,
      title,
      events,
      description,
      emptyDescription,
      danger = false,
      showProgress = false,
      expectedEventTypes = [],
      staticDate,
      staticActions = [],
      muted,
      statusTitle,
      statusColor,
      typeLabel,
      showStatus = true,
      completed,
      current,
      completedEventsCount,
      expectedEventsCount,
      preserveEmptyState = false
    }) {
      const metrics = this.getStepMetrics({ events, expectedEventTypes, staticDate, staticActions })
      const resolvedExpectedEventsCount = expectedEventsCount ?? metrics.expectedEventsCount
      const resolvedCompletedEventsCount = completedEventsCount ?? metrics.completedEventsCount
      const state = {
        ...this.getStepState({
          metrics: {
            ...metrics,
            completedEventsCount: resolvedCompletedEventsCount,
            expectedEventsCount: resolvedExpectedEventsCount
          },
          showProgress,
          danger,
          muted: Boolean(muted)
        })
      }

      if (completed !== undefined) {
        state.completed = completed
      }

      if (current !== undefined) {
        state.current = current
        state.inProgress = current
      }

      if (muted !== undefined) {
        state.muted = muted
      }

      const resolvedStatusColor = statusColor || this.getStepStatusColor(state)
      const rawDate = staticDate || this.getLatestDate(events)

      return {
        key,
        title,
        events,
        static: metrics.hasStaticContent,
        completed: state.completed,
        muted: state.muted,
        danger,
        current: state.current,
        showProgress,
        expectedEventsCount: resolvedExpectedEventsCount,
        completedEventsCount: resolvedCompletedEventsCount,
        rawDate,
        date: this.formatDate(rawDate),
        description: description || (metrics.hasEvents ? undefined : emptyDescription),
        statusTitle: this.getStepStatusTitle({ statusTitle, state }),
        statusColor: resolvedStatusColor,
        progressColor: resolvedStatusColor,
        connectorCompleted: state.completed,
        staticActions,
        showStatus,
        typeLabel,
        preserveEmptyState
      }
    },

    getStepMetrics({ events, expectedEventTypes, staticDate, staticActions }) {
      const eventTypes = events.map(event => this.getEventType(event))

      return {
        hasEvents: events.length > 0,
        completedEventsCount: expectedEventTypes.filter(type => eventTypes.includes(type)).length,
        expectedEventsCount: expectedEventTypes.length,
        hasStaticContent: Boolean(staticDate || staticActions.length)
      }
    },

    getStepState({ metrics, showProgress, danger, muted }) {
      const completed =
        showProgress && metrics.expectedEventsCount
          ? metrics.completedEventsCount === metrics.expectedEventsCount
          : (metrics.hasEvents || metrics.hasStaticContent) && !muted
      const awaitingReview = showProgress && !completed && metrics.completedEventsCount === 2
      const inProgress = showProgress && metrics.hasEvents && !completed && !awaitingReview
      const isMuted = muted || (!metrics.hasEvents && !danger && !metrics.hasStaticContent)

      return {
        completed,
        awaitingReview,
        inProgress,
        muted: isMuted,
        current: awaitingReview || inProgress,
        danger
      }
    },

    getStepStatusTitle({ statusTitle, state }) {
      if (statusTitle) return statusTitle
      if (state.muted) return 'Not Applicable'
      if (state.danger) return 'Separation'
      if (state.completed) return 'Completed'
      if (state.awaitingReview) return 'Awaiting review'

      return 'In progress'
    },

    getStepStatusColor(state) {
      if (state.muted) return STATUS_COLORS.muted
      if (state.danger) return STATUS_COLORS.danger
      if (state.completed) return STATUS_COLORS.completed
      if (state.awaitingReview) return STATUS_COLORS.awaitingReview
      if (state.current) return STATUS_COLORS.awaitingReview

      return STATUS_COLORS.inProgress
    },

    getStartTitle(invitationEvents) {
      const subType = invitationEvents.map(event => event.sub_type).find(Boolean)

      return START_LABELS[String(subType ?? '').toLowerCase()] || 'Created'
    },

    getStepDescription(title) {
      return STEP_DESCRIPTIONS[title]
    },

    buildStaticStep({ key, title, date, actions = [] }) {
      return this.buildStep({
        key,
        title,
        events: [],
        description: this.getStepDescription(title),
        staticDate: date,
        staticActions: actions,
        showStatus: false
      })
    },

    resolveEmptyStepDescriptions(steps) {
      const lastMeaningfulIndex = steps.reduce((lastIndex, step, index) => {
        if (step.events.length || step.completed || step.current || step.danger || step.staticActions.length) {
          return index
        }

        return lastIndex
      }, -1)

      return steps.map((step, index) => {
        if (
          !step.preserveEmptyState &&
          !step.static &&
          !step.events.length &&
          !step.staticActions.length &&
          !step.completed &&
          !step.current &&
          !step.danger
        ) {
          return {
            ...step,
            description: step.description || (index < lastMeaningfulIndex ? 'Not Applicable' : undefined),
            statusTitle: index < lastMeaningfulIndex ? 'Not Applicable' : undefined,
            showStatus: index < lastMeaningfulIndex
          }
        }

        return step
      })
    },

    sortStartSteps(steps) {
      return [...steps].sort((first, second) => {
        const firstDate = moment(first.date, 'MM/DD/YYYY')
        const secondDate = moment(second.date, 'MM/DD/YYYY')

        if (firstDate.isValid() && secondDate.isValid() && !firstDate.isSame(secondDate)) {
          return firstDate.valueOf() - secondDate.valueOf()
        }

        return 0
      })
    },

    shouldShowSeniorityStep(periodIndex, startStep) {
      if (periodIndex !== 0 || !this.seniorityDate || !startStep?.rawDate) return false

      const seniorityDate = this.parseDate(this.seniorityDate)
      const startDate = this.parseDate(startStep.rawDate)

      return Boolean(seniorityDate && startDate && seniorityDate.isBefore(startDate, 'day'))
    },

    getEventsByType(events, eventType) {
      return events.filter(event => this.getEventType(event) === eventType)
    },

    getEventsByTypes(events, eventTypes) {
      return events.filter(event => eventTypes.includes(this.getEventType(event)))
    },

    hasMiddleStepApproval(events) {
      return events.some(event => VIEW_EVENT_TYPES.includes(this.getEventType(event)))
    },

    buildInitialSkillCheckStep(index, trainingEvents) {
      const contextEvents = this.getEventsByTypes(trainingEvents, TRAINING_PERIOD_EVENT_TYPES)
      const skillCheckEvents = this.getEventsByType(trainingEvents, INITIAL_SKILL_CHECK_EVENT_TYPE)
      const events = this.sortEvents([...contextEvents, ...skillCheckEvents])
      const completed = skillCheckEvents.length > 0

      return this.buildStep({
        key: `period-${index}-initial-skill-check`,
        title: 'Initial Skill Check',
        events,
        description: this.getStepDescription('Initial Skill Check'),
        emptyDescription: undefined,
        showProgress: true,
        expectedEventTypes: [INITIAL_SKILL_CHECK_EVENT_TYPE],
        completedEventsCount: completed ? 1 : 0,
        expectedEventsCount: 1
      })
    },

    buildAnnualDueStep({ key, title, dueDate }) {
      const due = this.isDueDate(dueDate)

      return this.buildStep({
        key,
        title,
        events: [],
        description: this.getStepDescription(title),
        staticDate: dueDate,
        completed: false,
        current: due,
        muted: false,
        statusColor: due ? STATUS_COLORS.awaitingReview : STATUS_COLORS.pending,
        statusTitle: due ? 'In progress' : undefined,
        showStatus: due
      })
    },

    buildAnnualCompletedStep({ key, title, event }) {
      return this.buildStep({
        key,
        title,
        events: [event],
        description: this.getStepDescription(title),
        emptyDescription: undefined,
        completed: true,
        current: false
      })
    },

    buildAnnualCycleSteps({ index, title, eventType, baseDate, events, alwaysShow = false }) {
      const completedEvents = this.sortEvents(this.getEventsByType(events, eventType))
      const steps = completedEvents.map((event, eventIndex) =>
        this.buildAnnualCompletedStep({
          key: `period-${index}-${eventType}-${event.id || eventIndex}`,
          title,
          event
        })
      )
      const lastCompletedEvent = completedEvents[completedEvents.length - 1]
      const dueBaseDate = lastCompletedEvent?.created_at || baseDate

      if (!dueBaseDate) {
        return alwaysShow
          ? [
              this.buildStep({
                key: `period-${index}-${eventType}-pending`,
                title,
                events: [],
                description: this.getStepDescription(title),
                completed: false,
                current: false,
                muted: false,
                statusColor: STATUS_COLORS.pending,
                showStatus: false,
                preserveEmptyState: true
              })
            ]
          : steps
      }

      const dueDate = this.addYear(dueBaseDate)

      return [
        ...steps,
        this.buildAnnualDueStep({
          key: `period-${index}-${eventType}-next`,
          title,
          dueDate
        })
      ]
    },

    isTrainingDocumentCompleted(document) {
      const normalizedStatus = String(document.status ?? '').toLowerCase()

      return FINAL_STATUSES.includes(normalizedStatus)
    },

    getTrainingDocumentStatusColor(document) {
      const normalizedStatus = String(document.status ?? '').toLowerCase()

      if (FINAL_STATUSES.includes(normalizedStatus)) return STATUS_COLORS.completed
      if (DANGER_STATUSES.includes(normalizedStatus)) return STATUS_COLORS.danger
      if (normalizedStatus && normalizedStatus !== 'not started') return STATUS_COLORS.awaitingReview

      return STATUS_COLORS.pending
    },

    buildTrainingFlowEvent(document, index) {
      const completed = this.isTrainingDocumentCompleted(document)

      return {
        ...document,
        key: `training-flow-${document.signed_sequence_id || document.sequence_id || index}`,
        eventType: 'training_flow',
        name: document.name || 'Untitled',
        meta: [document.complete_date, document.sequence_id ? `Sequence #${document.sequence_id}` : null]
          .filter(Boolean)
          .join(' - '),
        completed,
        statusColor: this.getTrainingDocumentStatusColor(document),
        canOpen: Boolean(document.signed_sequence_id),
        openType: document.signed_sequence_id ? 'sequence' : undefined,
        object_id: document.signed_sequence_id,
        metadata: {},
        actions: document.signed_sequence_id ? [{ key: 'open', label: 'View' }] : []
      }
    },

    buildTrainingStep(index, middleStepApproved) {
      const documents = this.companyFlow?.trainings?.documents ?? []
      const events = documents.map((document, documentIndex) => this.buildTrainingFlowEvent(document, documentIndex))
      const completedEventsCount = events.filter(event => event.completed).length
      const hasEvents = events.length > 0
      const canStartTraining = middleStepApproved
      const completed = canStartTraining && hasEvents && completedEventsCount === events.length
      const inProgress = canStartTraining && hasEvents && !completed

      return this.buildStep({
        key: `period-${index}-training`,
        title: 'Training',
        events,
        description: this.getStepDescription('Training'),
        emptyDescription: undefined,
        showProgress: canStartTraining && hasEvents,
        completed,
        current: inProgress,
        muted: !canStartTraining || !hasEvents,
        statusTitle: !canStartTraining || !hasEvents ? 'Not Applicable' : completed ? 'Completed' : 'In progress',
        completedEventsCount,
        expectedEventsCount: events.length
      })
    },

    buildCafEvent(caf, index) {
      const status = String(caf.status ?? '').toLowerCase()
      const metadata = caf.metadata ?? {}
      const signedDocument = this.signedDocuments.find(
        document => Number(document.signed_version_id) === Number(caf.signed_document_id)
      )
      const documentId = caf.document_id ?? metadata.document_id ?? signedDocument?.id
      const versionNumber = caf.version_number ?? metadata.version_number ?? signedDocument?.current_version
      const canOpenDocument =
        documentId !== undefined && documentId !== null && versionNumber !== undefined && versionNumber !== null
      const statusMeta = {
        pending: { title: 'Pending', color: STATUS_COLORS.awaitingReview },
        signed: { title: 'Signed', color: STATUS_COLORS.completed },
        canceled: { title: 'Canceled', color: STATUS_COLORS.muted }
      }[status] ?? { title: 'Unknown', color: STATUS_COLORS.muted }

      return {
        ...caf,
        key: `caf-${caf.id ?? index}`,
        name: `CAF #${caf.id ?? index + 1}`,
        meta: this.formatDate(caf.created_at),
        metadata: {
          ...metadata,
          document_id: documentId,
          version_number: versionNumber
        },
        completed: status === 'signed',
        statusTitle: statusMeta.title,
        statusColor: statusMeta.color,
        openType: canOpenDocument ? 'document' : undefined,
        actions: canOpenDocument ? [{ key: 'open', label: 'View' }] : []
      }
    },

    buildCafStep(index, caf) {
      const events = this.sortEvents(caf).map((item, eventIndex) => this.buildCafEvent(item, eventIndex))
      const latestEvent = events[events.length - 1]
      const hasPending = events.some(event => String(event.status ?? '').toLowerCase() === 'pending')
      const latestStatus = String(latestEvent?.status ?? '').toLowerCase()

      return this.buildStep({
        key: `period-${index}-caf`,
        title: 'Change Action Forms',
        events,
        description: this.getStepDescription('Change Action Forms'),
        emptyDescription: undefined,
        completed: !hasPending && latestStatus === 'signed',
        current: hasPending,
        muted: !hasPending && latestStatus === 'canceled',
        statusTitle: hasPending ? 'Pending' : latestEvent?.statusTitle,
        statusColor: hasPending ? STATUS_COLORS.awaitingReview : latestEvent?.statusColor
      })
    },

    buildPeriod(rawPeriod, index) {
      const invitationEvents = this.buildEvents(
        [...(rawPeriod.invitation?.source ?? []), ...(rawPeriod.invitation?.documents ?? [])],
        `period-${index}-invitation`
      )
      const onboardingEvents = this.buildEvents(rawPeriod.onboarding?.events, `period-${index}-onboarding`)
      const trainingEvents = this.buildEvents(
        [...(rawPeriod.training?.events ?? []), ...(rawPeriod.training?.documents ?? [])],
        `period-${index}-training`
      )
      const terminationEvents = this.buildEvents(
        [...(rawPeriod.termination?.source ?? []), ...(rawPeriod.termination?.documents ?? [])],
        `period-${index}-termination`
      )
      const rehireEvents = this.buildEvents(rawPeriod.rehire?.events, `period-${index}-rehire`)
      const caf = rawPeriod.caf ?? []
      const periodKey = this.getPeriodKey(index)
      const allEvents = [
        ...invitationEvents,
        ...onboardingEvents,
        ...trainingEvents,
        ...terminationEvents,
        ...rehireEvents,
        ...caf
      ]
      const currentPeriod = index === this.lifecycle.length - 1
      const periodColor = PERIOD_COLORS[index % PERIOD_COLORS.length]
      const startStepTitle = this.getStartTitle(invitationEvents)
      const startStep = this.buildStep({
        key: `period-${index}-invitation`,
        title: startStepTitle,
        events: invitationEvents,
        description: this.getStepDescription(startStepTitle),
        emptyDescription: undefined
      })
      const seniorityStep = this.shouldShowSeniorityStep(index, startStep)
        ? this.buildStaticStep({
            key: `period-${index}-seniority`,
            title: 'Seniority',
            date: this.seniorityDate
          })
        : undefined
      const firstShiftStep =
        index === 0 && this.firstShiftDate
          ? this.buildStaticStep({
              key: `period-${index}-first-shift`,
              title: 'First Shift',
              date: this.firstShiftDate
            })
          : undefined
      const initialSkillCheckStep = this.isDynamicFormAvailable('skills_check')
        ? this.buildInitialSkillCheckStep(index, trainingEvents)
        : undefined
      const initialSkillCheckDate = this.getEventsByType(trainingEvents, INITIAL_SKILL_CHECK_EVENT_TYPE)[0]?.created_at
      const middleStepApproved = this.hasMiddleStepApproval(onboardingEvents)
      const annualSkillCheckSteps = this.isDynamicFormAvailable('annual_skills_check')
        ? this.buildAnnualCycleSteps({
            index,
            title: 'Annual Skill Check',
            eventType: ANNUAL_SKILL_CHECK_EVENT_TYPE,
            baseDate: initialSkillCheckDate,
            events: trainingEvents,
            alwaysShow: true
          })
        : []
      const annualPerformanceReviewSteps = this.isDynamicFormAvailable('performance_review')
        ? this.buildAnnualCycleSteps({
            index,
            title: 'Performance Review',
            eventType: PERFORMANCE_REVIEW_EVENT_TYPE,
            baseDate: undefined,
            events: trainingEvents,
            alwaysShow: true
          })
        : []
      const steps = [
        seniorityStep,
        startStep,
        firstShiftStep,
        this.buildStep({
          key: `period-${index}-onboarding`,
          title: this.middleStepTitle,
          events: this.resolveMiddleStepEvents(onboardingEvents),
          description: this.getStepDescription(this.middleStepTitle),
          emptyDescription: undefined,
          showProgress: true,
          expectedEventTypes: this.expectedMiddleEventTypes
        }),
        initialSkillCheckStep,
        ...annualSkillCheckSteps,
        ...annualPerformanceReviewSteps,
        this.buildTrainingStep(index, middleStepApproved),
        caf.length ? this.buildCafStep(index, caf) : undefined,
        this.buildStep({
          key: `period-${index}-termination`,
          title: 'Separation',
          events: terminationEvents,
          description: this.getStepDescription('Separation'),
          emptyDescription: undefined,
          danger: terminationEvents.length > 0,
          statusTitle: this.getTerminationStatusTitle(terminationEvents) || undefined
        }),
        rehireEvents.length
          ? this.buildStep({
              key: `period-${index}-rehire`,
              title: 'Rehire',
              events: rehireEvents,
              description: this.getStepDescription('Rehire'),
              emptyDescription: undefined
            })
          : undefined
      ].filter(Boolean)

      return {
        key: periodKey,
        title: `Cycle ${index + 1}${currentPeriod ? ' (Current)' : ''}`,
        color: periodColor,
        expanded: this.isPeriodExpanded(periodKey),
        dateRange: this.getPeriodDateRange(allEvents, currentPeriod),
        steps: this.resolveEmptyStepDescriptions(steps)
      }
    },

    sortMiddleStepEvents(events) {
      const priorities = new Map(this.expectedMiddleEventTypes.map((type, index) => [type, index]))

      return events
        .map((event, index) => ({ event, index }))
        .sort((first, second) => {
          const firstPriority = priorities.get(first.event.eventType) ?? priorities.size
          const secondPriority = priorities.get(second.event.eventType) ?? priorities.size

          return firstPriority - secondPriority || first.index - second.index
        })
        .map(({ event }) => event)
    },

    resolveMiddleStepEvents(events) {
      const sortedEvents = this.sortMiddleStepEvents(events)
      const lastEventIndex = sortedEvents.length - 1

      return sortedEvents.map((event, index) => {
        const actionLabel = REVIEW_EVENT_TYPES.includes(event.eventType)
          ? 'Review'
          : VIEW_EVENT_TYPES.includes(event.eventType)
            ? 'View'
            : undefined
        const actions =
          index === lastEventIndex && actionLabel
            ? event.actions.map(action => (action.key === 'open' ? { ...action, label: actionLabel } : action))
            : []

        return {
          ...event,
          actions,
          highlighted: actions.some(action => action.key === 'open')
        }
      })
    },

    getPeriodDateRange(events, currentPeriod) {
      const dates = this.sortEvents(events)
        .map(event => event.created_at)
        .filter(Boolean)

      if (!dates.length) return undefined

      const firstDate = this.formatDate(dates[0])
      const lastDate = this.formatDate(dates[dates.length - 1])
      const endDate = currentPeriod ? 'Now (Active)' : lastDate

      return firstDate === endDate ? firstDate : `${firstDate} - ${endDate}`
    }
  }
}
